import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface BackboneDetailsState {
    /** Applied VRF display names. Empty = no filter (show all). */
    vrfFilterSelection: string[];
    vrfs: string[];
    vrfCounts: Record<string, number>;
    /** Applied connector-type group ids (e.g. 'SDWAN' | 'Cloud' | 'Port'). Empty = no filter. */
    connectorTypeFilterSelection: string[];
    /** Connector-type group ids present in current backbone, in display order. */
    connectorTypes: string[];
    /** Counts per connector-type group id. */
    connectorTypeCounts: Record<string, number>;
    /** Applied BGP status filter option ids (`up` | `down`). Empty = no filter. */
    bgpStatusFilterSelection: string[];
    /** Counts of connectors with BGP up vs down (from BB-Connector `bgpStatus.isEstablished`). */
    bgpStatusCounts: Record<string, number>;
}

const initialState: BackboneDetailsState = {
    vrfFilterSelection: [],
    vrfs: [],
    vrfCounts: {},
    connectorTypeFilterSelection: [],
    connectorTypes: [],
    connectorTypeCounts: {},
    bgpStatusFilterSelection: [],
    bgpStatusCounts: {},
};

const backboneDetailsSlice = createSlice({
    name: 'backboneDetails',
    initialState,
    reducers: {
        setVrfFilterSelection: (state, action: PayloadAction<string[]>) => {
            state.vrfFilterSelection = [...action.payload];
        },
        setBackboneVrfs: (
            state,
            action: PayloadAction<{ vrfs: string[]; vrfCounts: Record<string, number> }>
        ) => {
            state.vrfs = action.payload.vrfs;
            state.vrfCounts = action.payload.vrfCounts;
        },
        setConnectorTypeFilterSelection: (state, action: PayloadAction<string[]>) => {
            state.connectorTypeFilterSelection = [...action.payload];
        },
        setBackboneConnectorTypes: (
            state,
            action: PayloadAction<{
                connectorTypes: string[];
                connectorTypeCounts: Record<string, number>;
            }>
        ) => {
            state.connectorTypes = action.payload.connectorTypes;
            state.connectorTypeCounts = action.payload.connectorTypeCounts;
        },
        setBgpStatusFilterSelection: (state, action: PayloadAction<string[]>) => {
            state.bgpStatusFilterSelection = [...action.payload];
        },
        setBackboneBgpStatusCounts: (
            state,
            action: PayloadAction<{ up: number; down: number }>
        ) => {
            state.bgpStatusCounts = {
                up: action.payload.up,
                down: action.payload.down,
            };
        },
        clearBackboneVrfs: (state) => {
            state.vrfs = [];
            state.vrfCounts = {};
            state.vrfFilterSelection = [];
            state.connectorTypes = [];
            state.connectorTypeCounts = {};
            state.connectorTypeFilterSelection = [];
            state.bgpStatusFilterSelection = [];
            state.bgpStatusCounts = {};
        },
    },
});

export const {
    setVrfFilterSelection,
    setBackboneVrfs,
    setConnectorTypeFilterSelection,
    setBackboneConnectorTypes,
    setBgpStatusFilterSelection,
    setBackboneBgpStatusCounts,
    clearBackboneVrfs,
} = backboneDetailsSlice.actions;
export default backboneDetailsSlice.reducer;
